import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Search, X, Plus, GripVertical, Trash2, User, Palette, 
  Calendar, RefreshCw, Clock, Sparkles, Edit2, CalendarClock,
  AlertCircle, CheckCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';

const API = '/api';

// Helper to format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

// Helper to format date for input
const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toISOString().slice(0, 16);
};

// Status badge component
const StatusBadge = ({ isActive, isScheduled, isExpired }) => {
  if (isExpired) {
    return (
      <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/50">
        <Clock className="w-3 h-3 mr-1" />
        Expired
      </Badge>
    );
  }
  if (isScheduled) {
    return (
      <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
        <CalendarClock className="w-3 h-3 mr-1" />
        Scheduled
      </Badge>
    );
  }
  if (isActive) {
    return (
      <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/50">
        <CheckCircle className="w-3 h-3 mr-1" />
        Active
      </Badge>
    );
  }
  return null;
};

// ===========================================
// FEATURED CREATORS MANAGER
// ===========================================
export const FeaturedCreatorsManager = () => {
  const [featuredCreators, setFeaturedCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCreator, setEditingCreator] = useState(null);
  const [featuredFrom, setFeaturedFrom] = useState('');
  const [featuredUntil, setFeaturedUntil] = useState('');

  const loadFeaturedCreators = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/featured-creators-list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setFeaturedCreators(response.data.featured_creators || []);
    } catch (error) {
      console.error('Failed to load featured creators:', error);
      toast.error('Failed to load featured creators');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFeaturedCreators();
  }, [loadFeaturedCreators]);

  const searchCreators = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/admin/search-creators`, {
        params: { q: query, limit: 20 },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Filter out already featured creators
      const featuredIds = new Set(featuredCreators.map(c => c.user_id));
      const filtered = (response.data.creators || []).filter(c => !featuredIds.has(c.user_id));
      setSearchResults(filtered);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const addCreator = async (creator) => {
    try {
      await axios.post(`${API}/admin/featured-creators-list`, {
        user_id: creator.user_id,
        featured_from: featuredFrom || null,
        featured_until: featuredUntil || null
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success(`Added ${creator.display_name || creator.handle} to featured list`);
      setShowAddDialog(false);
      setSearchQuery('');
      setSearchResults([]);
      setFeaturedFrom('');
      setFeaturedUntil('');
      loadFeaturedCreators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add creator');
    }
  };

  const updateCreator = async () => {
    if (!editingCreator) return;
    
    try {
      await axios.put(`${API}/admin/featured-creators-list/${editingCreator.user_id}`, {
        featured_from: featuredFrom || null,
        featured_until: featuredUntil || null
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Featured creator updated');
      setShowEditDialog(false);
      setEditingCreator(null);
      setFeaturedFrom('');
      setFeaturedUntil('');
      loadFeaturedCreators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update creator');
    }
  };

  const openEditDialog = (creator) => {
    setEditingCreator(creator);
    setFeaturedFrom(formatDateForInput(creator.featured_from));
    setFeaturedUntil(formatDateForInput(creator.featured_until));
    setShowEditDialog(true);
  };

  const removeCreator = async (userId, name) => {
    if (!confirm(`Remove ${name} from featured list?`)) return;
    
    try {
      await axios.delete(`${API}/admin/featured-creators-list/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Creator removed from featured list');
      loadFeaturedCreators();
    } catch (error) {
      toast.error('Failed to remove creator');
    }
  };

  const reorderCreators = async (fromIndex, toIndex) => {
    const newOrder = [...featuredCreators];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    
    setFeaturedCreators(newOrder);
    
    try {
      await axios.put(`${API}/admin/featured-creators-list/reorder`, {
        order: newOrder.map(c => c.user_id)
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (error) {
      toast.error('Failed to reorder');
      loadFeaturedCreators();
    }
  };

  const moveUp = (index) => {
    if (index > 0) reorderCreators(index, index - 1);
  };

  const moveDown = (index) => {
    if (index < featuredCreators.length - 1) reorderCreators(index, index + 1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800" data-testid="featured-creators-manager">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-400" />
          Featured Creators Manager
        </CardTitle>
        <CardDescription>
          Manually curate which creators appear in the &quot;Featured Creators&quot; homepage section.
          This list takes priority over perk-based featured creators.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add Creator Button */}
        <div className="flex gap-2">
          <Dialog open={showAddDialog} onOpenChange={(open) => {
            setShowAddDialog(open);
            if (!open) {
              setFeaturedFrom('');
              setFeaturedUntil('');
              setSearchQuery('');
              setSearchResults([]);
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-700" data-testid="add-featured-creator-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Featured Creator
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg" data-testid="add-creator-dialog">
              <DialogHeader>
                <DialogTitle>Add Creator to Featured List</DialogTitle>
              </DialogHeader>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    searchCreators(e.target.value);
                  }}
                  placeholder="Search by handle or email..."
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                  data-testid="search-creators-input"
                />
              </div>
              
              {/* Feature Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-zinc-400 mb-1 block">Feature From (Optional)</Label>
                  <Input
                    type="datetime-local"
                    value={featuredFrom}
                    onChange={(e) => setFeaturedFrom(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="creator-feature-from-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Leave empty to start immediately</p>
                </div>
                <div>
                  <Label className="text-sm text-zinc-400 mb-1 block">Feature Until (Optional)</Label>
                  <Input
                    type="datetime-local"
                    value={featuredUntil}
                    onChange={(e) => setFeaturedUntil(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="creator-feature-until-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Leave empty for permanent</p>
                </div>
              </div>
              
              <div className="bg-zinc-800/50 rounded-lg p-3 text-xs text-zinc-400 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <span>Content will automatically stop appearing after the &quot;Feature Until&quot; date without manual removal.</span>
              </div>
              
              {/* Search Results */}
              <div className="max-h-60 overflow-y-auto space-y-2" data-testid="creator-search-results">
                {searching && (
                  <div className="text-center py-4 text-zinc-400">Searching...</div>
                )}
                {!searching && searchResults.length === 0 && searchQuery && (
                  <div className="text-center py-4 text-zinc-400">No creators found</div>
                )}
                {searchResults.map((creator) => (
                  <div 
                    key={creator.user_id}
                    className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 cursor-pointer"
                    onClick={() => addCreator(creator)}
                    data-testid={`creator-result-${creator.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      {creator.avatar_url ? (
                        <img 
                          src={creator.avatar_url} 
                          alt={creator.display_name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                          <User className="w-5 h-5 text-zinc-500" />
                        </div>
                      )}
                      <div>
                        <h4 className="font-medium text-white">{creator.display_name}</h4>
                        <p className="text-sm text-zinc-400">@{creator.handle} • {creator.email}</p>
                      </div>
                    </div>
                    <Plus className="w-5 h-5 text-purple-400" />
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
          
          <Button 
            variant="outline" 
            className="border-zinc-700"
            onClick={loadFeaturedCreators}
            data-testid="refresh-featured-creators-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={(open) => {
          setShowEditDialog(open);
          if (!open) {
            setEditingCreator(null);
            setFeaturedFrom('');
            setFeaturedUntil('');
          }
        }}>
          <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md" data-testid="edit-creator-dialog">
            <DialogHeader>
              <DialogTitle>Edit Feature Schedule</DialogTitle>
            </DialogHeader>
            
            {editingCreator && (
              <div className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg mb-4">
                {editingCreator.avatar_url ? (
                  <img src={editingCreator.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                    <User className="w-5 h-5 text-zinc-500" />
                  </div>
                )}
                <div>
                  <h4 className="font-medium text-white">{editingCreator.display_name}</h4>
                  <p className="text-sm text-zinc-400">@{editingCreator.handle}</p>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-zinc-400 mb-1 block">Feature From</Label>
                <Input
                  type="datetime-local"
                  value={featuredFrom}
                  onChange={(e) => setFeaturedFrom(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-creator-feature-from"
                />
                <p className="text-xs text-zinc-500 mt-1">Leave empty to feature immediately</p>
              </div>
              <div>
                <Label className="text-sm text-zinc-400 mb-1 block">Feature Until</Label>
                <Input
                  type="datetime-local"
                  value={featuredUntil}
                  onChange={(e) => setFeaturedUntil(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-creator-feature-until"
                />
                <p className="text-xs text-zinc-500 mt-1">Leave empty for permanent featuring</p>
              </div>
            </div>
            
            <div className="bg-zinc-800/50 rounded-lg p-3 text-xs text-zinc-400 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <span>Content will automatically stop appearing after the &quot;Feature Until&quot; date.</span>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={updateCreator} className="bg-purple-600 hover:bg-purple-700" data-testid="save-creator-dates-btn">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Featured Creators List */}
        {featuredCreators.length === 0 ? (
          <div className="text-center py-12 text-zinc-400" data-testid="no-featured-creators">
            <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No featured creators yet</p>
            <p className="text-sm">Add creators to appear in the homepage section</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="featured-creators-list">
            {featuredCreators.map((creator, index) => (
              <div 
                key={creator.user_id}
                className={`flex items-center gap-3 p-3 rounded-lg group ${
                  creator.is_expired ? 'bg-zinc-800/50 opacity-60' : 
                  creator.is_scheduled ? 'bg-zinc-800/80' : 'bg-zinc-800'
                }`}
                data-testid={`featured-creator-item-${creator.user_id}`}
              >
                {/* Drag Handle & Position */}
                <div className="flex flex-col items-center gap-1">
                  <button 
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    className="text-zinc-500 hover:text-white disabled:opacity-30"
                    data-testid={`move-creator-up-${creator.user_id}`}
                  >
                    ▲
                  </button>
                  <span className="text-xs text-zinc-500 w-6 text-center">{index + 1}</span>
                  <button 
                    onClick={() => moveDown(index)}
                    disabled={index === featuredCreators.length - 1}
                    className="text-zinc-500 hover:text-white disabled:opacity-30"
                    data-testid={`move-creator-down-${creator.user_id}`}
                  >
                    ▼
                  </button>
                </div>
                
                {/* Avatar */}
                {creator.avatar_url ? (
                  <img 
                    src={creator.avatar_url} 
                    alt={creator.display_name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-zinc-700 flex items-center justify-center">
                    <User className="w-6 h-6 text-zinc-500" />
                  </div>
                )}
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-white">{creator.display_name}</h4>
                    <StatusBadge 
                      isActive={creator.is_active} 
                      isScheduled={creator.is_scheduled} 
                      isExpired={creator.is_expired} 
                    />
                  </div>
                  <p className="text-sm text-zinc-400">@{creator.handle}</p>
                  {(creator.featured_from || creator.featured_until) && (
                    <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {creator.featured_from && <span>From: {formatDate(creator.featured_from)}</span>}
                      {creator.featured_from && creator.featured_until && <span> • </span>}
                      {creator.featured_until && <span>Until: {formatDate(creator.featured_until)}</span>}
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-zinc-400 hover:text-white hover:bg-zinc-700"
                    onClick={() => openEditDialog(creator)}
                    data-testid={`edit-creator-${creator.user_id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    onClick={() => removeCreator(creator.user_id, creator.display_name)}
                    data-testid={`remove-creator-${creator.user_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ===========================================
// FEATURED ARTWORKS MANAGER
// ===========================================
export const FeaturedArtworksManager = () => {
  const [featuredArtworks, setFeaturedArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [featuredFrom, setFeaturedFrom] = useState('');
  const [featuredUntil, setFeaturedUntil] = useState('');

  const loadFeaturedArtworks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/featured-artworks-list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setFeaturedArtworks(response.data.featured_artworks || []);
    } catch (error) {
      console.error('Failed to load featured artworks:', error);
      toast.error('Failed to load featured artworks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFeaturedArtworks();
  }, [loadFeaturedArtworks]);

  const searchArtworks = async (query) => {
    setSearching(true);
    try {
      const response = await axios.get(`${API}/admin/search-artworks`, {
        params: { q: query, limit: 20 },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Filter out already featured artworks
      const featuredIds = new Set(featuredArtworks.map(a => a.artwork_id));
      const filtered = (response.data.artworks || []).filter(a => !featuredIds.has(a.artwork_id));
      setSearchResults(filtered);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const addArtwork = async (artwork) => {
    try {
      await axios.post(`${API}/admin/featured-artworks-list`, {
        artwork_id: artwork.artwork_id,
        featured_from: featuredFrom || null,
        featured_until: featuredUntil || null
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success(`Added "${artwork.title}" to featured artworks`);
      setShowAddDialog(false);
      setSearchQuery('');
      setSearchResults([]);
      setFeaturedFrom('');
      setFeaturedUntil('');
      loadFeaturedArtworks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add artwork');
    }
  };

  const updateArtwork = async () => {
    if (!editingArtwork) return;
    
    try {
      await axios.put(`${API}/admin/featured-artworks-list/${editingArtwork.artwork_id}`, {
        featured_from: featuredFrom || null,
        featured_until: featuredUntil || null
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Featured artwork updated');
      setShowEditDialog(false);
      setEditingArtwork(null);
      setFeaturedFrom('');
      setFeaturedUntil('');
      loadFeaturedArtworks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update artwork');
    }
  };

  const openEditDialog = (artwork) => {
    setEditingArtwork(artwork);
    setFeaturedFrom(formatDateForInput(artwork.featured_from));
    setFeaturedUntil(formatDateForInput(artwork.featured_until));
    setShowEditDialog(true);
  };

  const removeArtwork = async (artworkId, title) => {
    if (!confirm(`Remove "${title}" from featured artworks?`)) return;
    
    try {
      await axios.delete(`${API}/admin/featured-artworks-list/${artworkId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Artwork removed from featured list');
      loadFeaturedArtworks();
    } catch (error) {
      toast.error('Failed to remove artwork');
    }
  };

  const reorderArtworks = async (fromIndex, toIndex) => {
    const newOrder = [...featuredArtworks];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    
    setFeaturedArtworks(newOrder);
    
    try {
      await axios.put(`${API}/admin/featured-artworks-list/reorder`, {
        order: newOrder.map(a => a.artwork_id)
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (error) {
      toast.error('Failed to reorder');
      loadFeaturedArtworks();
    }
  };

  const moveUp = (index) => {
    if (index > 0) reorderArtworks(index, index - 1);
  };

  const moveDown = (index) => {
    if (index < featuredArtworks.length - 1) reorderArtworks(index, index + 1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-pink-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800 mt-6" data-testid="featured-artworks-manager">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Palette className="w-5 h-5 text-pink-400" />
          Featured Artworks Manager
        </CardTitle>
        <CardDescription>
          Select individual artworks to spotlight in a dedicated homepage carousel/grid.
          Set optional start and end dates for automatic expiration.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add Artwork Button */}
        <div className="flex gap-2">
          <Dialog open={showAddDialog} onOpenChange={(open) => {
            setShowAddDialog(open);
            if (open) {
              searchArtworks('');
            } else {
              setFeaturedFrom('');
              setFeaturedUntil('');
              setSearchQuery('');
              setSearchResults([]);
            }
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-pink-600 hover:bg-pink-700"
                data-testid="add-featured-artwork-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Featured Artwork
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="add-artwork-dialog">
              <DialogHeader>
                <DialogTitle>Add Artwork to Featured List</DialogTitle>
              </DialogHeader>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    searchArtworks(e.target.value);
                  }}
                  placeholder="Search artworks by title..."
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                  data-testid="search-artworks-input"
                />
              </div>
              
              {/* Feature Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-zinc-400 mb-1 block">Feature From (Optional)</Label>
                  <Input
                    type="datetime-local"
                    value={featuredFrom}
                    onChange={(e) => setFeaturedFrom(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="artwork-feature-from-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-zinc-400 mb-1 block">Feature Until (Optional)</Label>
                  <Input
                    type="datetime-local"
                    value={featuredUntil}
                    onChange={(e) => setFeaturedUntil(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="artwork-feature-until-input"
                  />
                </div>
              </div>
              
              <div className="bg-zinc-800/50 rounded-lg p-3 text-xs text-zinc-400 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <span>Content will automatically stop appearing after the &quot;Feature Until&quot; date without manual removal.</span>
              </div>
              
              {/* Search Results Grid */}
              <div className="flex-1 overflow-y-auto mt-4" data-testid="artwork-search-results">
                {searching && (
                  <div className="text-center py-8 text-zinc-400">Searching...</div>
                )}
                {!searching && searchResults.length === 0 && (
                  <div className="text-center py-8 text-zinc-400">
                    {searchQuery ? 'No artworks found' : 'Type to search artworks'}
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {searchResults.map((artwork) => (
                    <div 
                      key={artwork.artwork_id}
                      className="relative group cursor-pointer rounded-lg overflow-hidden bg-zinc-800"
                      onClick={() => addArtwork(artwork)}
                      data-testid={`artwork-result-${artwork.artwork_id}`}
                    >
                      <img 
                        src={artwork.thumbnail_url || artwork.image_url} 
                        alt={artwork.title}
                        className="w-full aspect-square object-cover"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2">
                        <Plus className="w-8 h-8 text-white mb-2" />
                        <p className="text-sm text-white text-center line-clamp-2">{artwork.title}</p>
                        {artwork.creator_handle && (
                          <p className="text-xs text-zinc-400">@{artwork.creator_handle}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          <Button 
            variant="outline" 
            className="border-zinc-700"
            onClick={loadFeaturedArtworks}
            data-testid="refresh-featured-artworks-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={(open) => {
          setShowEditDialog(open);
          if (!open) {
            setEditingArtwork(null);
            setFeaturedFrom('');
            setFeaturedUntil('');
          }
        }}>
          <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md" data-testid="edit-artwork-dialog">
            <DialogHeader>
              <DialogTitle>Edit Feature Schedule</DialogTitle>
            </DialogHeader>
            
            {editingArtwork && (
              <div className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg mb-4">
                <img 
                  src={editingArtwork.thumbnail_url || editingArtwork.image_url} 
                  alt={editingArtwork.title}
                  className="w-16 h-16 rounded object-cover"
                />
                <div>
                  <h4 className="font-medium text-white">{editingArtwork.title}</h4>
                  {editingArtwork.creator_handle && (
                    <p className="text-sm text-zinc-400">@{editingArtwork.creator_handle}</p>
                  )}
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-zinc-400 mb-1 block">Feature From</Label>
                <Input
                  type="datetime-local"
                  value={featuredFrom}
                  onChange={(e) => setFeaturedFrom(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-artwork-feature-from"
                />
                <p className="text-xs text-zinc-500 mt-1">Leave empty to feature immediately</p>
              </div>
              <div>
                <Label className="text-sm text-zinc-400 mb-1 block">Feature Until</Label>
                <Input
                  type="datetime-local"
                  value={featuredUntil}
                  onChange={(e) => setFeaturedUntil(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-artwork-feature-until"
                />
                <p className="text-xs text-zinc-500 mt-1">Leave empty for permanent featuring</p>
              </div>
            </div>
            
            <div className="bg-zinc-800/50 rounded-lg p-3 text-xs text-zinc-400 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <span>Content will automatically stop appearing after the &quot;Feature Until&quot; date.</span>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={updateArtwork} className="bg-pink-600 hover:bg-pink-700" data-testid="save-artwork-dates-btn">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Featured Artworks Grid */}
        {featuredArtworks.length === 0 ? (
          <div className="text-center py-12 text-zinc-400" data-testid="no-featured-artworks">
            <Palette className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No featured artworks yet</p>
            <p className="text-sm">Add artworks to appear in the homepage spotlight</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="featured-artworks-list">
            {featuredArtworks.map((artwork, index) => (
              <div 
                key={artwork.artwork_id}
                className={`relative group rounded-lg overflow-hidden ${
                  artwork.is_expired ? 'opacity-50' : 
                  artwork.is_scheduled ? 'opacity-75' : ''
                }`}
                data-testid={`featured-artwork-item-${artwork.artwork_id}`}
              >
                <img 
                  src={artwork.thumbnail_url || artwork.image_url} 
                  alt={artwork.title}
                  className="w-full aspect-square object-cover"
                />
                
                {/* Position Badge */}
                <div className="absolute top-2 left-2 bg-black/70 rounded-full w-6 h-6 flex items-center justify-center">
                  <span className="text-xs font-bold text-white">{index + 1}</span>
                </div>
                
                {/* Status Badge */}
                <div className="absolute top-2 right-2">
                  {artwork.is_expired && (
                    <Badge variant="outline" className="bg-red-500/80 text-white border-0 text-xs">
                      Expired
                    </Badge>
                  )}
                  {artwork.is_scheduled && !artwork.is_expired && (
                    <Badge variant="outline" className="bg-yellow-500/80 text-black border-0 text-xs">
                      Scheduled
                    </Badge>
                  )}
                </div>
                
                {/* Date Info */}
                {(artwork.featured_from || artwork.featured_until) && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 text-xs text-white">
                    {artwork.featured_until && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>Until: {formatDate(artwork.featured_until)}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Hover Actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                  <div className="flex gap-1">
                    <button 
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="p-1.5 bg-zinc-700 rounded hover:bg-zinc-600 disabled:opacity-30"
                      data-testid={`move-artwork-up-${artwork.artwork_id}`}
                    >
                      ▲
                    </button>
                    <button 
                      onClick={() => moveDown(index)}
                      disabled={index === featuredArtworks.length - 1}
                      className="p-1.5 bg-zinc-700 rounded hover:bg-zinc-600 disabled:opacity-30"
                      data-testid={`move-artwork-down-${artwork.artwork_id}`}
                    >
                      ▼
                    </button>
                    <button 
                      onClick={() => openEditDialog(artwork)}
                      className="p-1.5 bg-zinc-700 rounded hover:bg-zinc-600"
                      data-testid={`edit-artwork-${artwork.artwork_id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => removeArtwork(artwork.artwork_id, artwork.title)}
                      className="p-1.5 bg-red-600 rounded hover:bg-red-500"
                      data-testid={`remove-artwork-${artwork.artwork_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm text-white text-center px-2 line-clamp-2">{artwork.title}</p>
                  {artwork.creator_handle && (
                    <p className="text-xs text-zinc-400">@{artwork.creator_handle}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default { FeaturedCreatorsManager, FeaturedArtworksManager };
